import React from 'react'

import { ReactComponent as NestedProjectIcon } from '@sketch/icons/folder-closed-17'

import { HighlightedText, TableComponents, Skeleton } from '@sketch/components'

import { useNestedProjectDropShares } from '../../hooks'

// This component shares styles with DocumentItem
import {
  ImageWrapper,
  Name,
  TableCell,
  TableWrapper,
  NameWrapper,
  DroppableTableWrapper,
  DroppableTableWrapperDragTooltip,
  SelectedCheckboxWrapper,
  SelectedCheckbox,
} from '../../../shares/components/DocumentItem/DocumentItem.styles'

import { NestedProjectItemProps } from './types'
import { PreviewSubtitle } from './PreviewSubtitle'
import { useSearch } from '@sketch/utils'
import { useFlag } from '@sketch/modules-common'
import { isNestedProject } from '../../../shares/components/ShareActionsDropdown'
import { ProjectStatusIcon } from './NestedProjectItem.styles'
import { getButtonProps } from './utils'

export const NestedProjectItemListView = (props: NestedProjectItemProps) => {
  const {
    className,
    nestedProject,
    onClick,
    workspaceIdentifier,
    renderDropdown,
    disabledDrag,
    onSelectChange,
    selected,
  } = props

  const {
    isDraggedOver = false,
    isDraggedOverItself = false,
    isDroppableAreaEnabled,
    ...dragAndDropEvents
  } = useNestedProjectDropShares({
    projectIdentifier: nestedProject.identifier,
    workspaceId: workspaceIdentifier,
    disabled: disabledDrag || nestedProject.isOptimistic,
  })

  const isMultipleSelectionOn = useFlag('multiple-selection')

  const a11yProps = onClick ? getButtonProps(onClick) : {}
  const dropdownContent = renderDropdown?.()
  const { search } = useSearch()

  const isDroppable =
    isDroppableAreaEnabled && isDraggedOver && !isDraggedOverItself

  if (!isNestedProject(nestedProject)) return null

  return (
    <DroppableTableWrapper
      $isDraggedOver={isDroppable}
      $isSelected={selected}
      className={className}
      title={nestedProject.name}
      data-project-id={nestedProject.identifier}
      {...dragAndDropEvents}
      {...a11yProps}
    >
      <TableCell>
        {isDroppable && (
          <DroppableTableWrapperDragTooltip>
            Add to folder
          </DroppableTableWrapperDragTooltip>
        )}
        <NameWrapper>
          {isMultipleSelectionOn && (
            <SelectedCheckboxWrapper onClick={event => event.stopPropagation()}>
              <span className="sr-only">
                {selected ? 'Unselect project' : 'Select project'}
              </span>
              <SelectedCheckbox
                type="checkbox"
                checked={selected}
                onChange={event => onSelectChange?.(event.target.checked)}
              />
            </SelectedCheckboxWrapper>
          )}

          <ImageWrapper title={nestedProject.name} data-drag-image>
            <ProjectStatusIcon
              as={NestedProjectIcon}
              aria-label={`Folder ${nestedProject.name}`}
            />
          </ImageWrapper>
          <Name>
            <HighlightedText search={search}>
              {nestedProject.name}
            </HighlightedText>
          </Name>
        </NameWrapper>
      </TableCell>
      <TableCell>
        <PreviewSubtitle
          nestedProjectsCount={nestedProject.nestedProjectsCount}
          sharesCount={nestedProject.sharesCount}
        />
      </TableCell>
      <TableComponents.TableCellSticky>
        {dropdownContent}
      </TableComponents.TableCellSticky>
    </DroppableTableWrapper>
  )
}

export interface NestedProjectItemListViewSkeletonProps {
  inViewListener?: React.ReactNode
}
export const NestedProjectItemListViewSkeleton = (
  props: NestedProjectItemListViewSkeletonProps
) => {
  const { inViewListener } = props
  return (
    <TableWrapper>
      <TableCell>
        <NameWrapper>
          <ImageWrapper>
            <ProjectStatusIcon as={NestedProjectIcon} aria-label="Folder" />
          </ImageWrapper>
          <Skeleton height="12px" width="30%" />
        </NameWrapper>
      </TableCell>
      <TableCell>
        <Skeleton height="12px" width="30%" />
        {inViewListener}
      </TableCell>
      <TableCell></TableCell>
    </TableWrapper>
  )
}
